<template>
    <AppModal
        v-if="user?.id"
        id="api-key-management-modal"
        ref="modalRef"
        size="lg"
        :modelValue="isModalVisible"
        @update:modelValue="onUpdateModelValue"
    >
        <template #header>
            <h3 class="modal-title">
                {{ user.email }} <br />
                <small class="fw-normal">{{ user.first_name }} {{ user.last_name }}</small>
            </h3>
        </template>

        <template #default>
            <AppAlert
                color="danger"
                :visible="errorMessage !== undefined"
                :dismissible="true"
                @click="errorMessage = undefined"
            >
                {{ errorMessage }}
            </AppAlert>

            <template v-if="typeof apiKey === 'undefined'">
                <AppLoading :loading="true" />
            </template>

            <template v-else-if="apiKey?.client_id">

                <AppAlert
                    color="success"
                    :visible="showSecurityWarning"
                    @click="showSecurityWarning = false"
                >
                    {{ $t('messages.generate_api_key_secure_store_credentials_warning_message') }}
                </AppAlert>

                <table class="table" style="border-collapse: separate; border-spacing: 1rem;">
                    <tr>
                        <td style="width: 100px; font-weight: bolder;">{{ $t('Client ID') }}</td>
                        <td>{{ apiKey.client_id }}</td>
                        <td class="text-end" style="width: 50px;" v-if="apiKey?.client_secret">
                            <AppIcon
                                style="cursor: pointer;"
                                :icon="copyIconClientId"
                                v-tooltip="tooltipTextClientId"
                                @click="copyToClipboard(apiKey.client_id, 'client_id')"
                            />
                        </td>
                    </tr>
                    <tr>
                        <td style="font-weight: bolder;">{{ $t('Client Secret') }}</td>
                        <td
                            v-if="apiKey?.client_secret"
                            style="overflow-wrap: anywhere;"
                        >
                            {{ apiKey.client_secret }}
                        </td>
                        <td v-else>************************************************************</td>
                        <td class="text-end" v-if="apiKey?.client_secret">
                            <AppIcon
                                style="cursor: pointer;"
                                :icon="copyIconClientSecret"
                                v-tooltip="tooltipTextClientSecret"
                                @click="copyToClipboard(apiKey.client_secret, 'client_secret')"
                            />
                        </td>
                    </tr>
                </table>
            </template>

        </template>

        <template #footer>
            <AppButton
                type="button"
                btnClass="btn-white"
                @click="closeModal"
                :is-disabled="isGenerating || isRegenerating || isRevoking"
            >
                {{ $t('Close') }}
            </AppButton>
            <template v-if="typeof apiKey === 'undefined'">
            </template>
            <template v-else-if="!apiKey?.client_id">
                <AppButton
                    type="button"
                    class="btn-primary"
                    :is-loading="isGenerating"
                    @click="generate"
                >
                    {{ $t('Generate API key') }}
                </AppButton>
            </template>
            <template v-else>
                <AppButton
                    type="button"
                    class="btn-primary"
                    :is-loading="isRegenerating"
                    :is-disabled="isRevoking"
                    @click="regenerate"
                >
                    {{ $t('Regenerate API key') }}
                </AppButton>
                <AppButton
                    type="button"
                    class="btn-primary"
                    :is-loading="isRevoking"
                    :is-disabled="isRegenerating"
                    @click="revoke"
                >
                    {{ $t('Revoke API key') }}
                </AppButton>
            </template>
        </template>
    </AppModal>
</template>

<script setup>
import { ref, watch } from "vue";
import {trans as $t} from "@/vue/composables/missingTranslations";
import AppModal from "../../../shared/AppModal.vue";
import AppButton from "@/vue/shared/AppButton.vue";
import { createConfirmDialog } from "@/vue/helpers";
import AppLoading from "@/vue/shared/AppLoading.vue";
import AppIcon from "@/vue/shared/AppIcon.vue";
import { faCheck, faCopy } from "@fortawesome/free-solid-svg-icons";
import AppAlert from "@/vue/shared/AppAlert.vue";

const { reveal: confirm } = createConfirmDialog();

const props = defineProps({
    user: {
        type: Object,
        required: true
    },
    isVisible: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['onGenerated', 'onRegenerated', 'onRevoked']);

const isModalVisible = ref(props.isVisible);
const modalRef = ref(null);

const showSecurityWarning = ref(false);
const errorMessage = ref(undefined);

const isGenerating = ref(false);
const isRegenerating = ref(false);
const isRevoking = ref(false);

const user = ref(props.user);
const apiKey = ref(undefined);

const copyIconClientId = ref(faCopy);
const tooltipTextClientId = ref($t("Copy to clipboard"));

const copyIconClientSecret = ref(faCopy);
const tooltipTextClientSecret = ref($t("Copy to clipboard"));

const closeModal = () => {
    modalRef.value.closeModal();
};

const onUpdateModelValue = (state) => {
    if (!state) {
        isModalVisible.value = false;
        showSecurityWarning.value = false;
        apiKey.value = undefined;
    }
};

const generate = async () => {
    const { isCanceled } = await confirm({
        title: $t("Confirm key generation"),
        message: $t('messages.confirm_key_generation_message')+
            "<br /><br />" +
            $t('Are you sure you wish to continue?'),
        confirmButtonText: $t("Confirm")
    });

    if (isCanceled) return;

    isGenerating.value = true;
    showSecurityWarning.value = false;
    errorMessage.value = undefined;

    axios.post(`/api/api-key/${user.value.id}/generate`, {}).then((response) => {
        apiKey.value = response.data;
        showSecurityWarning.value = true;

        emit('onGenerated');
    }).catch((error) => {
        if (error?.response?.data?.message) {
            errorMessage.value = error.response.data.message;
        } else {
            errorMessage.value = $t("API Key generation failed. Try again later");
        }
    }).finally(() => isGenerating.value = false);
};

const regenerate = async () => {
    const { isCanceled } = await confirm({
        title: $t("Confirm key regeneration"),
        message: $t('messages.confirm_key_regeneration_message') +
            "<br /><br />" +
            $t('Are you sure you wish to continue?'),
        confirmButtonText: $t("Confirm")
    });

    if (isCanceled) return;

    isRegenerating.value = true;
    showSecurityWarning.value = false;
    errorMessage.value = undefined;

    axios.post(`/api/api-key/${user.value.id}/regenerate`, {}).then((response) => {
        apiKey.value = response.data;
        showSecurityWarning.value = true;

        emit('onRegenerated');
    }).catch((error) => {
        if (error?.response?.data?.message) {
            errorMessage.value = error.response.data.message;
        } else {
            errorMessage.value = $t("API Key regeneration failed. Try again later");
        }
    }).finally(() => isRegenerating.value = false);
};

const revoke = async () => {
    const { isCanceled } = await confirm({
        title: $t("Confirm key revocation"),
        message: $t('messages.confirm_key_revocation_message') +
            "<br /><br />" +
            $t('Are you sure you wish to continue?'),
        confirmButtonText: $t("Confirm")
    });

    if (isCanceled) return;

    isRevoking.value = true;
    showSecurityWarning.value = false;
    errorMessage.value = undefined;

    axios.post(`/api/api-key/${user.value.id}/revoke`, {}).then((response) => {
        apiKey.value = {};

        emit('onRevoked');
    }).catch((error) => {
        if (error?.response?.data?.message) {
            errorMessage.value = error.response.data.message;
        } else {
            errorMessage.value = $t("API Key revocation failed. Try again later");
        }
    }).finally(() => isRevoking.value = false);
};

const copyToClipboard = (text, type) => {
    const iconRef = type === "client_id" ? copyIconClientId : copyIconClientSecret;
    const tooltipRef = type === "client_id" ? tooltipTextClientId : tooltipTextClientSecret;

    navigator.clipboard.writeText(text).then(() => {
        iconRef.value = faCheck;
        tooltipRef.value = $t("Copied!");

        setTimeout(() => {
            iconRef.value = faCopy;
            tooltipRef.value = $t("Copy to clipboard");
        }, 5000);
    }).catch((error) => {
        console.error("Could not copy text to clipboard: ", error);
    });
};

watch(() => props.user, (newValue) => {
    user.value = newValue;

    if (! user.value?.id) {
        return;
    }

    axios.get(`/api/api-key/${user.value.id}/get`, {}).then((response) => {
        apiKey.value = response.data;
    }).catch(console.error);
});

watch(() => props.isVisible, (newValue) => {
    isModalVisible.value = newValue;
});

</script>

<style>
#api-key-management-modal .modal-body {
    padding: 1rem !important;
}
</style>
